/**
 * SpineTelos — one SHAPE sector's TELOS setup screen (the Spine §1, path A + B).
 * Two entry paths — Quick survey or Build it yourself — both land on the same
 * three-tier cascade (Vision → Goal/deity+level → Aim+Protocol). Survey answers
 * pre-select the cascade; the user can still change everything. See
 * docs/OLMPS_SPINE_SPEC.md §1 and spine-types.ts for the contracts this
 * screen fills in.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { SECTOR_LABELS, type Sector } from '../domain/olmps';
import { Atmosphere, GlassPanel, GlassPill, PrimaryButton } from './glass';
import { SHAPE_CASCADE } from './shapeCascade';
import { SECTOR_SURVEY, foldSectorSurvey } from './sectorSurvey';
import { saveSectorSetup } from './spine-data';
import {
  SECTOR_SURVEY_MIN,
  SECTOR_SURVEY_MAX,
  type SectorDeity,
  type SpineAimOption,
  type SpineSectorSetup,
  type SurveyOption,
} from './spine-types';

type Step = 'entry' | 'survey' | 'vision' | 'goal' | 'aim';

interface AimDraft {
  id: string; // aim id, or `custom-${n}` for custom aims
  text: string;
  protocol: { id: string; text: string; on: boolean }[];
}

export function SpineTelos({
  userId,
  sector,
  onDone,
}: {
  userId: string | undefined;
  sector: Sector;
  onDone: () => void;
}) {
  const styles = useStyles();
  const cascade = SHAPE_CASCADE[sector];
  const survey = SECTOR_SURVEY[sector];
  const sectorName = SECTOR_LABELS[sector];

  const [step, setStep] = useState<Step>('entry');
  const [saving, setSaving] = useState(false);

  // survey state
  const [surveyIndex, setSurveyIndex] = useState(0);
  const [surveyChosen, setSurveyChosen] = useState<SurveyOption[]>([]);

  // vision tier
  const [visionId, setVisionId] = useState<string | null>(null);
  const [customVision, setCustomVision] = useState('');
  const [visionMode, setVisionMode] = useState<'pick' | 'custom'>('pick');

  // goal tier
  const [deityId, setDeityId] = useState<string | null>(null);
  const [deityLevel, setDeityLevel] = useState(1);

  // aim tier
  const [aims, setAims] = useState<AimDraft[]>([]);
  const [customAimText, setCustomAimText] = useState('');
  const [customAimParts, setCustomAimParts] = useState<string[]>(['']);

  // Defensive full reset whenever the sector changes. The parent keys this
  // component by sector so it normally remounts fresh, but Fast Refresh can
  // preserve state across a hot reload — this guarantees a new sector always
  // starts clean at 'entry' (Endeavour), never inheriting the previous
  // sector's step/aims/answers. No-op on first mount (state is already fresh).
  useEffect(() => {
    setStep('entry');
    setSaving(false);
    setSurveyIndex(0);
    setSurveyChosen([]);
    setVisionId(null);
    setCustomVision('');
    setVisionMode('pick');
    setDeityId(null);
    setDeityLevel(1);
    setAims([]);
    setCustomAimText('');
    setCustomAimParts(['']);
  }, [sector]);

  const visionText = visionMode === 'custom' ? customVision.trim() : cascade.visions.find((v) => v.id === visionId)?.text ?? '';
  const selectedDeity: SectorDeity | undefined = cascade.deities.find((d) => d.id === deityId);
  const selectedLevel = selectedDeity?.levels.find((l) => l.level === deityLevel) ?? selectedDeity?.levels[0];

  const canFinishSurvey = surveyChosen.length >= SECTOR_SURVEY_MIN;
  const surveyDone = surveyIndex >= survey.length || surveyIndex >= SECTOR_SURVEY_MAX;

  // ── survey path ───────────────────────────────────────────────────────

  const answerSurvey = (opt: SurveyOption) => {
    const next = [...surveyChosen, opt];
    setSurveyChosen(next);
    if (surveyIndex + 1 >= survey.length || surveyIndex + 1 >= SECTOR_SURVEY_MAX) {
      landFromSurvey(next);
      return;
    }
    setSurveyIndex(surveyIndex + 1);
  };

  const finishSurveyEarly = () => landFromSurvey(surveyChosen);

  const landFromSurvey = (chosen: SurveyOption[]) => {
    const suggestion = foldSectorSurvey(sector, chosen);
    if (suggestion.visionId) {
      setVisionMode('pick');
      setVisionId(suggestion.visionId);
    }
    if (suggestion.deityId) {
      setDeityId(suggestion.deityId);
      setDeityLevel(1);
    }
    if (suggestion.aimIds.length) {
      setAims(
        suggestion.aimIds
          .map((id) => cascade.aims.find((a) => a.id === id))
          .filter((a): a is SpineAimOption => !!a)
          .slice(0, 3)
          .map((a) => ({
            id: a.id,
            text: a.text,
            protocol: a.protocol.map((p) => ({ id: p.id, text: p.text, on: true })),
          }))
      );
    }
    setStep('vision');
  };

  // ── vision tier ───────────────────────────────────────────────────────

  const pickVision = (id: string) => {
    setVisionMode('pick');
    setVisionId(id);
  };

  const canAdvanceVision = visionMode === 'custom' ? customVision.trim().length > 0 : !!visionId;

  // ── goal tier ─────────────────────────────────────────────────────────

  const pickDeity = (id: string) => {
    setDeityId(id);
    setDeityLevel(1);
  };

  // ── aim tier ──────────────────────────────────────────────────────────

  const toggleAim = (opt: SpineAimOption) => {
    const exists = aims.find((a) => a.id === opt.id);
    if (exists) {
      setAims(aims.filter((a) => a.id !== opt.id));
      return;
    }
    if (aims.length >= 3) return;
    setAims([
      ...aims,
      { id: opt.id, text: opt.text, protocol: opt.protocol.map((p) => ({ id: p.id, text: p.text, on: true })) },
    ]);
  };

  const togglePart = (aimId: string, partId: string) => {
    setAims(
      aims.map((a) =>
        a.id === aimId
          ? { ...a, protocol: a.protocol.map((p) => (p.id === partId ? { ...p, on: !p.on } : p)) }
          : a
      )
    );
  };

  const addCustomPartRow = () => setCustomAimParts([...customAimParts, '']);
  const setCustomPart = (i: number, text: string) => {
    const next = [...customAimParts];
    next[i] = text;
    setCustomAimParts(next);
  };

  const addCustomAim = () => {
    const text = customAimText.trim();
    if (!text || aims.length >= 3) return;
    const parts = customAimParts.map((p) => p.trim()).filter(Boolean);
    setAims([
      ...aims,
      {
        id: `custom-${Date.now()}`,
        text,
        protocol: parts.map((p, i) => ({ id: `custom-part-${i}`, text: p, on: true })),
      },
    ]);
    setCustomAimText('');
    setCustomAimParts(['']);
  };

  const canFinish = aims.length > 0;

  const finish = async () => {
    if (!userId || !canFinish) return;
    setSaving(true);
    const setup: SpineSectorSetup = {
      visionText,
      deityId: deityId ?? null,
      deityLevel,
      aims: aims.map((a) => ({ text: a.text, protocol: a.protocol.filter((p) => p.on).map((p) => p.text) })),
      done: true,
    };
    await saveSectorSetup(supabase, userId, sector, setup);
    onDone();
  };

  // ── render ────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Atmosphere />
      <ScrollView contentContainerStyle={styles.scroll}>
        <Text style={styles.kicker}>ATLAS · {sectorName}</Text>

        {step === 'entry' && (
          <EntryStep
            sectorName={sectorName}
            onSurvey={() => setStep('survey')}
            onBuild={() => setStep('vision')}
          />
        )}

        {step === 'survey' && !surveyDone && survey[surveyIndex] && (
          <SurveyStep
            question={survey[surveyIndex]}
            index={surveyIndex}
            total={survey.length}
            canFinish={canFinishSurvey}
            onAnswer={answerSurvey}
            onFinishEarly={finishSurveyEarly}
            onBack={() => setStep('entry')}
          />
        )}

        {step === 'vision' && (
          <VisionStep
            visions={cascade.visions}
            visionId={visionId}
            visionMode={visionMode}
            customVision={customVision}
            onPick={pickVision}
            onCustomMode={() => setVisionMode('custom')}
            onCustomText={setCustomVision}
            canAdvance={canAdvanceVision}
            onBack={() => setStep('entry')}
            onNext={() => setStep('goal')}
          />
        )}

        {step === 'goal' && (
          <GoalStep
            deities={cascade.deities}
            deityId={deityId}
            deityLevel={deityLevel}
            onPickDeity={pickDeity}
            onPickLevel={setDeityLevel}
            onBack={() => setStep('vision')}
            onNext={() => setStep('aim')}
          />
        )}

        {step === 'aim' && (
          <AimStep
            aimOptions={cascade.aims}
            aims={aims}
            customAimText={customAimText}
            customAimParts={customAimParts}
            onToggleAim={toggleAim}
            onTogglePart={togglePart}
            onCustomText={setCustomAimText}
            onCustomPart={setCustomPart}
            onAddCustomPartRow={addCustomPartRow}
            onAddCustomAim={addCustomAim}
            onBack={() => setStep('goal')}
            canFinish={canFinish}
            saving={saving}
            onFinish={finish}
          />
        )}
      </ScrollView>
    </View>
  );
}

// ── Entry step ──────────────────────────────────────────────────────────

function EntryStep({
  sectorName,
  onSurvey,
  onBuild,
}: {
  sectorName: string;
  onSurvey: () => void;
  onBuild: () => void;
}) {
  const styles = useStyles();
  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.title}>Set your {sectorName} direction.</Text>
      <Text style={styles.body}>
        Pick a path — a quick survey that suggests a direction, or build it yourself from
        scratch. Either way you land on the same setup and can change anything.
      </Text>
      <View style={styles.stack}>
        <PrimaryButton label="Quick survey (recommended)" onPress={onSurvey} />
        <GlassPill label="Build it yourself" onPress={onBuild} />
      </View>
    </GlassPanel>
  );
}

// ── Survey step ─────────────────────────────────────────────────────────

function SurveyStep({
  question,
  index,
  total,
  canFinish,
  onAnswer,
  onFinishEarly,
  onBack,
}: {
  question: { prompt: string; options: SurveyOption[] };
  index: number;
  total: number;
  canFinish: boolean;
  onAnswer: (opt: SurveyOption) => void;
  onFinishEarly: () => void;
  onBack: () => void;
}) {
  const styles = useStyles();
  // One-time pop the moment Submit becomes available (at SECTOR_SURVEY_MIN).
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!canFinish) return;
    pulse.setValue(1);
    Animated.sequence([
      Animated.timing(pulse, { toValue: 1.16, duration: 170, useNativeDriver: true }),
      Animated.spring(pulse, { toValue: 1, friction: 3.5, tension: 140, useNativeDriver: true }),
    ]).start();
  }, [canFinish, pulse]);

  return (
    <GlassPanel strong style={styles.panel}>
      <SurveyProgress answered={index} total={total} />
      <Text style={styles.stepKicker}>
        Question {index + 1} of {Math.min(total, SECTOR_SURVEY_MAX)}
      </Text>
      <Text style={styles.title}>{question.prompt}</Text>
      <View style={styles.stack}>
        {question.options.map((opt) => (
          <Pressable key={opt.label} onPress={() => onAnswer(opt)} style={styles.option}>
            <Text style={styles.optionText}>{opt.label}</Text>
          </Pressable>
        ))}
      </View>
      {canFinish && (
        <Animated.View style={{ alignSelf: 'center', transform: [{ scale: pulse }] }}>
          <Pressable onPress={onFinishEarly} style={styles.finishEarly}>
            <Text style={styles.finishEarlyText}>Submit now — more sharpens it, but you can stop here.</Text>
          </Pressable>
        </Animated.View>
      )}
      <BackLink onPress={onBack} />
    </GlassPanel>
  );
}

// An encouraging progress meter for the survey. Fills as questions are
// answered; a notch marks the point (SECTOR_SURVEY_MIN) where the user can
// submit, and the caption keeps them moving toward it, then rewards depth.
function SurveyProgress({ answered, total }: { answered: number; total: number }) {
  const styles = useStyles();
  const maxQ = Math.min(total, SECTOR_SURVEY_MAX);
  const pct = Math.max(0, Math.min(1, answered / maxQ));
  const notchPct = Math.min(1, SECTOR_SURVEY_MIN / maxQ);
  const reachedMin = answered >= SECTOR_SURVEY_MIN;
  const remainingToMax = maxQ - answered;

  let caption: string;
  if (!reachedMin) {
    const left = SECTOR_SURVEY_MIN - answered;
    caption = left === 1 ? 'One more to a read you can use.' : `${left} more to a read you can use.`;
  } else if (remainingToMax <= 0) {
    caption = 'Full-depth read — nicely done. ✦';
  } else {
    caption = `Enough for a read ✓ — ${remainingToMax} more sharpens it.`;
  }

  return (
    <View style={styles.progressWrap}>
      <View style={styles.progressTrack}>
        <View style={[styles.progressFill, { width: `${pct * 100}%` }]} />
        <View style={[styles.progressNotch, { left: `${notchPct * 100}%` }, reachedMin && styles.progressNotchDone]} />
      </View>
      <Text style={[styles.progressCaption, reachedMin && styles.progressCaptionDone]}>{caption}</Text>
    </View>
  );
}

// ── Vision step ─────────────────────────────────────────────────────────

function VisionStep({
  visions,
  visionId,
  visionMode,
  customVision,
  onPick,
  onCustomMode,
  onCustomText,
  canAdvance,
  onBack,
  onNext,
}: {
  visions: { id: string; text: string }[];
  visionId: string | null;
  visionMode: 'pick' | 'custom';
  customVision: string;
  onPick: (id: string) => void;
  onCustomMode: () => void;
  onCustomText: (t: string) => void;
  canAdvance: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const styles = useStyles();
  const { c } = useTheme();
  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.stepKicker}>Tier 1 · Vision (Endeavour)</Text>
      <Text style={styles.title}>What are you building toward?</Text>
      <View style={styles.stack}>
        {visions.map((v) => (
          <Pressable
            key={v.id}
            onPress={() => onPick(v.id)}
            style={[styles.option, visionMode === 'pick' && visionId === v.id && styles.optionActive]}
          >
            <Text style={[styles.optionText, visionMode === 'pick' && visionId === v.id && styles.optionTextActive]}>
              {v.text}
            </Text>
          </Pressable>
        ))}
        <Pressable onPress={onCustomMode} style={[styles.option, visionMode === 'custom' && styles.optionActive]}>
          <Text style={[styles.optionText, visionMode === 'custom' && styles.optionTextActive]}>Write my own…</Text>
        </Pressable>
        {visionMode === 'custom' && (
          <TextInput
            value={customVision}
            onChangeText={onCustomText}
            placeholder="Having…"
            placeholderTextColor={c.textFaint}
            style={styles.input}
            multiline
          />
        )}
      </View>
      <View style={styles.row}>
        <BackLink onPress={onBack} />
        <PrimaryButton small label="Next" onPress={onNext} disabled={!canAdvance} />
      </View>
    </GlassPanel>
  );
}

// ── Goal step ───────────────────────────────────────────────────────────

function GoalStep({
  deities,
  deityId,
  deityLevel,
  onPickDeity,
  onPickLevel,
  onBack,
  onNext,
}: {
  deities: SectorDeity[];
  deityId: string | null;
  deityLevel: number;
  onPickDeity: (id: string) => void;
  onPickLevel: (level: number) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const styles = useStyles();
  const selected = deities.find((d) => d.id === deityId);
  const level = selected?.levels.find((l) => l.level === deityLevel) ?? selected?.levels[0];

  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.stepKicker}>Tier 2 · Goal (skillset)</Text>
      <Text style={styles.title}>Which skillset are you building?</Text>
      <View style={styles.stack}>
        {deities.map((d) => (
          <Pressable
            key={d.id}
            onPress={() => onPickDeity(d.id)}
            style={[styles.option, deityId === d.id && styles.optionActive]}
          >
            <Text style={[styles.optionText, styles.optionTextBold, deityId === d.id && styles.optionTextActive]}>
              {d.habit} <Text style={styles.optionDeity}>({d.name})</Text>
            </Text>
            <Text style={[styles.optionSub, deityId === d.id && styles.optionTextActive]}>{d.domain}</Text>
          </Pressable>
        ))}
      </View>

      {selected && (
        <View style={styles.stack}>
          <Text style={styles.subKicker}>Level</Text>
          <View style={styles.levelRow}>
            {selected.levels.map((l) => (
              <GlassPill
                key={l.level}
                label={`L${l.level}`}
                active={deityLevel === l.level}
                onPress={() => onPickLevel(l.level)}
              />
            ))}
          </View>
          {level && (
            <View style={styles.levelDetail}>
              <Text style={styles.levelTitle}>{level.title}</Text>
              {level.skills.map((s) => (
                <Text key={s} style={styles.levelSkill}>
                  · {s}
                </Text>
              ))}
            </View>
          )}
        </View>
      )}

      <View style={styles.row}>
        <BackLink onPress={onBack} />
        <PrimaryButton small label="Next" onPress={onNext} disabled={!selected} />
      </View>
    </GlassPanel>
  );
}

// ── Aim step ────────────────────────────────────────────────────────────

function AimStep({
  aimOptions,
  aims,
  customAimText,
  customAimParts,
  onToggleAim,
  onTogglePart,
  onCustomText,
  onCustomPart,
  onAddCustomPartRow,
  onAddCustomAim,
  onBack,
  canFinish,
  saving,
  onFinish,
}: {
  aimOptions: SpineAimOption[];
  aims: AimDraft[];
  customAimText: string;
  customAimParts: string[];
  onToggleAim: (opt: SpineAimOption) => void;
  onTogglePart: (aimId: string, partId: string) => void;
  onCustomText: (t: string) => void;
  onCustomPart: (i: number, t: string) => void;
  onAddCustomPartRow: () => void;
  onAddCustomAim: () => void;
  onBack: () => void;
  canFinish: boolean;
  saving: boolean;
  onFinish: () => void;
}) {
  const styles = useStyles();
  const { c } = useTheme();
  const atCap = aims.length >= 3;

  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.stepKicker}>Tier 3 · Aim &amp; Protocol</Text>
      <Text style={styles.title}>Pick up to 3 recurring aims.</Text>
      <View style={styles.stack}>
        {aimOptions.map((opt) => {
          const active = aims.find((a) => a.id === opt.id);
          return (
            <View key={opt.id} style={[styles.option, active && styles.optionActive]}>
              <Pressable onPress={() => onToggleAim(opt)} disabled={!active && atCap}>
                <Text style={[styles.optionText, styles.optionTextBold, active && styles.optionTextActive]}>
                  {active ? '✓ ' : ''}
                  {opt.text}
                </Text>
              </Pressable>
              {active && (
                <View style={styles.protocolList}>
                  {active.protocol.map((p) => (
                    <Pressable key={p.id} onPress={() => onTogglePart(opt.id, p.id)} style={styles.protocolRow}>
                      <Text style={[styles.protocolText, !p.on && styles.protocolTextOff]}>
                        {p.on ? '☑' : '☐'} {p.text}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          );
        })}

        {!atCap && (
          <View style={styles.customAim}>
            <Text style={styles.subKicker}>Custom aim</Text>
            <TextInput
              value={customAimText}
              onChangeText={onCustomText}
              placeholder="A recurring action of my own…"
              placeholderTextColor={c.textFaint}
              style={styles.input}
            />
            {customAimParts.map((p, i) => (
              <TextInput
                key={i}
                value={p}
                onChangeText={(t) => onCustomPart(i, t)}
                placeholder={`Protocol part ${i + 1}`}
                placeholderTextColor={c.textFaint}
                style={styles.input}
              />
            ))}
            <View style={styles.row}>
              <GlassPill label="+ another part" onPress={onAddCustomPartRow} />
              <GlassPill label="Add aim" onPress={onAddCustomAim} disabled={!customAimText.trim()} />
            </View>
          </View>
        )}
      </View>

      <View style={styles.row}>
        <BackLink onPress={onBack} />
        <PrimaryButton label="Finish" onPress={onFinish} disabled={!canFinish || saving} />
      </View>
    </GlassPanel>
  );
}

// ── shared bits ─────────────────────────────────────────────────────────

function BackLink({ onPress }: { onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={styles.backLink}>
      <Text style={styles.backLinkText}>← Back</Text>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  container: { flex: 1 },
  scroll: { padding: 20, gap: 16, paddingBottom: 60 },
  kicker: { fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: c.textFaint, textAlign: 'center' },
  stepKicker: { fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: c.purple, fontWeight: '700' },
  subKicker: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: c.textFaint, marginTop: 6 },
  panel: { gap: 14 },
  title: { fontSize: 19, fontWeight: '700', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  body: { fontSize: 14, color: c.textMuted, lineHeight: 20 },
  stack: { gap: 10 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },

  option: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 4,
    ...neu.raisedSm,
  },
  optionActive: { backgroundColor: c.lime, ...neu.inset },
  optionText: { fontSize: 14, color: c.slate },
  optionTextBold: { fontWeight: '700', color: c.ink },
  optionDeity: { fontWeight: '600', color: c.textMuted, fontSize: 12 },

  progressWrap: { marginBottom: 14, gap: 6 },
  progressTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: t.track,
    overflow: 'visible',
    justifyContent: 'center',
  },
  progressFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 4,
    backgroundColor: t.gold,
    ...neu.glowPrimary,
  },
  progressNotch: {
    position: 'absolute',
    width: 2,
    top: -3,
    bottom: -3,
    marginLeft: -1,
    borderRadius: 1,
    backgroundColor: t.dotOff,
  },
  progressNotchDone: { backgroundColor: c.primaryDeep },
  progressCaption: { fontSize: 12, fontWeight: '700', color: c.slate },
  progressCaptionDone: { color: c.blueText },
  optionTextActive: { color: c.blueText },
  optionSub: { fontSize: 12, color: c.textMuted },

  input: {
    borderRadius: radii.tile,
    backgroundColor: c.inputFill,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: c.ink,
    ...neu.insetDeep,
  },

  levelRow: { flexDirection: 'row', gap: 8 },
  levelDetail: { gap: 4, paddingTop: 4 },
  levelTitle: { fontSize: 14, fontWeight: '700', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  levelSkill: { fontSize: 12, color: c.textMuted },

  protocolList: { marginTop: 8, gap: 6, paddingLeft: 4 },
  protocolRow: { paddingVertical: 2 },
  protocolText: { fontSize: 13, color: c.slate },
  protocolTextOff: { color: c.baseDark, textDecorationLine: 'line-through' },

  customAim: { gap: 8, paddingTop: 6 },

  finishEarly: {
    borderRadius: radii.pill,
    borderWidth: 1.5,
    borderColor: t.lapsed,
    backgroundColor: t.glass,
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  finishEarlyText: { fontSize: 13, fontWeight: '800', color: t.lapsed, textAlign: 'center', letterSpacing: 0.3 },

  backLink: { paddingVertical: 8, paddingRight: 8 },
  backLinkText: { fontSize: 14, color: c.textMuted, fontWeight: '600' },
}),
);
